import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Мессенджерное поведение ленты сообщений: старые сверху, новые снизу,
 * открываемся на самом свежем, догрузка старых не дёргает вьюпорт (ТЗ 0032).
 *
 * Механизм жил в треде диалога (`CommunicationsPanel`), а лента канала была
 * «вверх ногами» — новые сверху при поле ввода снизу. Вынесен сюда, чтобы обе
 * ленты вели себя одинаково не на словах: две копии разъехались бы на первой
 * же правке.
 *
 * Четыре тонкости, каждая оплачена измерением в браузере:
 *
 * 1. **Ключ автоскролла — id самого нового сообщения, а не длина списка.**
 *    Догрузка старых страниц тоже увеличивает длину, и на длине вьюпорт
 *    прыгал бы вниз ровно тогда, когда пользователь листает вверх.
 * 2. **Инвариант якоря — расстояние до НИЗА, а не приращение высоты.** Строки
 *    размечены `content-visibility: auto`, поэтому сразу после рендера
 *    `scrollHeight` — ещё оценка, и «прибавить разницу высот» даёт ноль.
 *    Расстояние до низа при дописывании сверху не меняется по определению.
 * 3. **Восстанавливать позицию нужно в layout-эффекте по счётчику строк, а
 *    НЕ в `requestAnimationFrame` после `fetchMore`.** Промис Apollo
 *    резолвится раньше, чем React коммитит новый DOM: замер показал, что
 *    лента выросла на 2190 px, а оба кадра `rAF` отработали ещё по старой
 *    высоте и не сдвинули `scrollTop` вообще.
 * 4. **Письма дорастают после коммита** (iframe меряет себя сам) — лента
 *    «приклеена» к низу, пока человек не листнул вверх (перепроверка U1).
 * 5. **Контейнер монтируется ПОЗЖЕ данных** (глубокая ссылка, первая
 *    загрузка: тред пришёл раньше списка диалогов). Эффект по объектному ref
 *    отрабатывал с `ref.current === null`, выходил, а его зависимости после
 *    монтирования уже не менялись — тред открывался на самом старом
 *    сообщении (повторная проверка глазами 07.10). Поэтому ref — callback:
 *    смонтированный узел кладётся в state и перезапускает эффект.
 */
interface IArgs {
  /** Смена контекста (диалог/канал) — проскроллить в низ заново. */
  resetKey: string;
  /** Id самого нового загруженного сообщения. */
  newestId: string;
  /** Сколько строк отрисовано — по нему ловится момент домержа страницы. */
  count: number;
}

interface IMessengerScroll<T extends HTMLElement> {
  /** Повесить на скроллящийся контейнер ленты (callback-ref, см. п. 5). */
  ref: (node: T | null) => void;
  /** Обернуть догрузку старых сообщений, чтобы вьюпорт остался на месте. */
  keepAnchored: (load: () => Promise<unknown>) => void;
}

/** `ResizeObserver` — в lib TypeScript проекта его ещё нет (как у EmailHtmlFrame). */
interface IResizeObserverLike {
  observe: (target: Element) => void;
  disconnect: () => void;
}
type TResizeObserverCtor = new (callback: () => void) => IResizeObserverLike;

const resizeObserverCtor = (): TResizeObserverCtor | undefined =>
  ((window as unknown) as { ResizeObserver?: TResizeObserverCtor })
    .ResizeObserver;

/**
 * Насколько близко к низу ещё считается «внизу» (px): дробные высоты и
 * округление `scrollTop` не должны отклеивать ленту от низа.
 */
const BOTTOM_SLACK_PX = 48;

export const useMessengerScroll = <T extends HTMLElement = HTMLDivElement>({
  resetKey,
  newestId,
  count,
}: IArgs): IMessengerScroll<T> => {
  const ref = useRef<T | null>(null);
  /** Смонтированный контейнер — в state, чтобы его появление будило эффект. */
  const [container, setContainer] = useState<T | null>(null);
  const setRef = useCallback((node: T | null) => {
    ref.current = node;
    setContainer(node);
  }, []);
  /** Расстояние до низа, замороженное на время догрузки старых сообщений. */
  const anchor = useRef<number | null>(null);
  /**
   * Лента «приклеена» к низу (перепроверка U1). Письма рисуются в iframe,
   * который узнаёт свою высоту ПОСЛЕ коммита: прокрутка в низ в
   * layout-эффекте попадала в ещё низкую ленту, потом письма вырастали на
   * тысячи пикселей — и тред почты открывался на самом старом сообщении.
   * Пока человек сам не листнул вверх, рост содержимого возвращает ленту в
   * низ (`ResizeObserver` на строках и на самой ленте).
   */
  const stick = useRef(true);
  /** Последний известный `scrollTop` — «листнул вверх» = он уменьшился. */
  const lastTop = useRef(0);

  useLayoutEffect(() => {
    const el = container;
    if (!el) return undefined;

    const toBottom = () => {
      el.scrollTop = el.scrollHeight;
      lastTop.current = el.scrollTop;
    };

    if (anchor.current !== null) {
      // Домержилась страница старых сообщений: возвращаем ту же дистанцию до
      // низа. Второй проход в rAF — на случай, если ленивые высоты
      // (`content-visibility`) досчитались уже после коммита.
      const restore = anchor.current;
      el.scrollTop = el.scrollHeight - restore;
      lastTop.current = el.scrollTop;
      requestAnimationFrame(() => {
        const node = ref.current;
        if (node) {
          node.scrollTop = node.scrollHeight - restore;
          lastTop.current = node.scrollTop;
        }
        anchor.current = null;
      });
    } else {
      // Новый контекст, первая страница или новое сообщение — в низ, и
      // держимся там, пока содержимое догоняет свою высоту.
      stick.current = true;
      toBottom();
    }

    // Рост содержимого НЕ двигает `scrollTop` — поэтому «листнул вверх» —
    // это именно его уменьшение, а не «далеко от низа»: иначе выросшее
    // письмо само бы отклеило ленту.
    const onScroll = () => {
      if (anchor.current !== null) {
        lastTop.current = el.scrollTop;
        return;
      }
      const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (fromBottom <= BOTTOM_SLACK_PX) stick.current = true;
      else if (el.scrollTop < lastTop.current - 1) stick.current = false;
      lastTop.current = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    let observer: IResizeObserverLike | null = null;
    const Observer = resizeObserverCtor();
    if (Observer) {
      observer = new Observer(() => {
        if (anchor.current !== null) {
          el.scrollTop = el.scrollHeight - anchor.current;
          lastTop.current = el.scrollTop;
        } else if (stick.current) {
          toBottom();
        }
      });
      // Сам контейнер — тоже: композер под лентой меняет высоту (подпись
      // ящика догрузилась, вложение прикрепили), лента ужимается, а строки
      // при этом не меняются.
      observer.observe(el);
      Array.from(el.children).forEach((child) => observer?.observe(child));
    }

    return () => {
      el.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [resetKey, newestId, count, container]);

  const keepAnchored = useCallback((load: () => Promise<unknown>) => {
    const el = ref.current;
    anchor.current = el ? el.scrollHeight - el.scrollTop : 0;
    // Человек сам полез в историю — от низа ленту отклеиваем.
    stick.current = false;
    load()
      // ТЗ 0033: якорь снимаем и на УСПЕХЕ, если страница не принесла ни одной
      // новой строки. Иначе ни одна зависимость layout-эффекта не меняется, он
      // не срабатывает, якорь остаётся заполненным — и СЛЕДУЮЩИЙ диалог
      // открывается посередине ленты вместо самого свежего сообщения.
      .then(() => {
        const node = ref.current;
        if (!node || anchor.current === null) return;
        if (node.scrollHeight - node.scrollTop === anchor.current) {
          anchor.current = null;
        }
      })
      .catch(() => {
        anchor.current = null;
      });
  }, []);

  return { ref: setRef, keepAnchored };
};
